/**
 * ControlBar — FE-011: User Controls
 * 5 action buttons: Next, Mute, Camera Toggle, Report, End
 * Touch-friendly sizing (44px min tap targets), keyboard shortcuts,
 * haptic feedback, inline SVG icons.
 */
import {
  type Component,
  createSignal,
  createEffect,
  onCleanup,
  Show,
} from 'solid-js';
import {
  IconNext,
  IconMicOn,
  IconMicOff,
  IconCameraOn,
  IconCameraOff,
  IconReport,
  IconEnd,
} from './Icons';
import ReportSheet from './ReportSheet';
import type { ReportCategory } from './ReportSheet';
import { showToast } from './Toast';
import styles from './ControlBar.module.css';

export interface ControlBarProps {
  /** Whether the session is currently connected */
  isConnected: () => boolean;
  /** Whether we are searching for the next match */
  isFindingNext: () => boolean;
  /** Local media stream for track toggling */
  localStream: MediaStream | null;
  /** Callback: skip to next match */
  onNext: () => void;
  /** Callback: report with category */
  onReport: (category: ReportCategory) => void;
  /** Callback: end session entirely */
  onEnd: () => void;
}

/**
 * Attempt haptic feedback via Vibration API.
 * Fails silently on unsupported devices.
 */
function haptic(duration = 10): void {
  try {
    if (navigator.vibrate) {
      navigator.vibrate(duration);
    }
  } catch {
    // Vibration API not supported or blocked — silent fail
  }
}

const ControlBar: Component<ControlBarProps> = (props) => {
  const [isMuted, setIsMuted] = createSignal(false);
  const [isCameraOff, setIsCameraOff] = createSignal(false);
  const [showReport, setShowReport] = createSignal(false);

  // --- Mute toggle ---
  const handleMute = () => {
    haptic();
    const stream = props.localStream;
    if (!stream) return;

    const audioTracks = stream.getAudioTracks();
    const newMuted = !isMuted();
    setIsMuted(newMuted);

    for (const track of audioTracks) {
      track.enabled = !newMuted;
    }

    showToast(newMuted ? 'Microphone muted' : 'Microphone unmuted', 'info', 2000);
  };

  // --- Camera toggle ---
  const handleCameraToggle = () => {
    haptic();
    const stream = props.localStream;
    if (!stream) return;

    const videoTracks = stream.getVideoTracks();
    const newCameraOff = !isCameraOff();
    setIsCameraOff(newCameraOff);

    for (const track of videoTracks) {
      track.enabled = !newCameraOff;
    }

    showToast(newCameraOff ? 'Camera turned off' : 'Camera turned on', 'info', 2000);
  };

  // --- Next ---
  const handleNext = () => {
    haptic(15);
    props.onNext();
  };

  // --- Report ---
  const handleReportOpen = () => {
    haptic();
    setShowReport(true);
  };

  const handleReportSubmit = (category: ReportCategory) => {
    setShowReport(false);
    haptic(20);
    props.onReport(category);
    showToast('Report submitted. Finding next person...', 'success', 3000);
  };

  const handleReportClose = () => {
    setShowReport(false);
  };

  // --- End ---
  const handleEnd = () => {
    haptic(15);
    props.onEnd();
  };

  // --- Keyboard shortcuts (desktop) ---
  createEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input or the report modal is open
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        showReport()
      ) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'n':
          if (props.isConnected()) {
            e.preventDefault();
            handleNext();
          }
          break;
        case 'm':
          e.preventDefault();
          handleMute();
          break;
        case 'c':
          e.preventDefault();
          handleCameraToggle();
          break;
        case 'escape':
          e.preventDefault();
          handleEnd();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    onCleanup(() => document.removeEventListener('keydown', handleKeyDown));
  });

  return (
    <>
      <div class={styles.bar} role="toolbar" aria-label="Call controls">
        {/* End call button */}
        <button
          class={`${styles.controlBtn} ${styles.endBtn}`}
          onClick={handleEnd}
          type="button"
          aria-label="End call (Escape)"
          title="End call (Esc)"
        >
          <IconEnd size={22} />
          <span class={styles.label}>End</span>
        </button>

        {/* Mute toggle */}
        <button
          class={`${styles.controlBtn} ${isMuted() ? styles.toggled : ''}`}
          onClick={handleMute}
          type="button"
          aria-label={isMuted() ? 'Unmute microphone (M)' : 'Mute microphone (M)'}
          aria-pressed={isMuted()}
          title={isMuted() ? 'Unmute (M)' : 'Mute (M)'}
        >
          <Show when={isMuted()} fallback={<IconMicOn size={22} />}>
            <IconMicOff size={22} />
          </Show>
          <span class={styles.label}>{isMuted() ? 'Unmute' : 'Mute'}</span>
        </button>

        {/* Next button — prominent center */}
        <button
          class={`${styles.controlBtn} ${styles.nextBtn}`}
          onClick={handleNext}
          disabled={props.isFindingNext()}
          type="button"
          aria-label="Next person (N)"
          title="Next person (N)"
        >
          <Show
            when={!props.isFindingNext()}
            fallback={<span class={styles.miniSpinner} aria-hidden="true" />}
          >
            <IconNext size={24} />
          </Show>
          <span class={styles.label}>
            {props.isFindingNext() ? 'Finding...' : 'Next'}
          </span>
        </button>

        {/* Camera toggle */}
        <button
          class={`${styles.controlBtn} ${isCameraOff() ? styles.toggled : ''}`}
          onClick={handleCameraToggle}
          type="button"
          aria-label={isCameraOff() ? 'Turn camera on (C)' : 'Turn camera off (C)'}
          aria-pressed={isCameraOff()}
          title={isCameraOff() ? 'Camera on (C)' : 'Camera off (C)'}
        >
          <Show when={isCameraOff()} fallback={<IconCameraOn size={22} />}>
            <IconCameraOff size={22} />
          </Show>
          <span class={styles.label}>{isCameraOff() ? 'Cam On' : 'Cam Off'}</span>
        </button>

        {/* Report button */}
        <button
          class={`${styles.controlBtn} ${styles.reportBtn}`}
          onClick={handleReportOpen}
          disabled={!props.isConnected()}
          type="button"
          aria-label="Report user"
          title="Report user"
        >
          <IconReport size={22} />
          <span class={styles.label}>Report</span>
        </button>
      </div>

      {/* Report bottom sheet */}
      <ReportSheet
        isOpen={showReport()}
        onClose={handleReportClose}
        onReport={handleReportSubmit}
      />
    </>
  );
};

export default ControlBar;
